import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { EVENT_PHOTOS } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Editorial masonry-style event photo strip.
// Uses a bespoke asymmetric layout — deliberately not a uniform grid.
export default function InTheField() {
  // Photos merged from Mongo (admin-uploaded) + the built-in fallback set.
  // Server photos ordered by `order` are shown first; fallback fills any gap.
  const [serverPhotos, setServerPhotos] = useState([]);
  useEffect(() => {
    axios
      .get(`${API}/event-photos`, { params: { target: "in-the-field" } })
      .then((r) => Array.isArray(r.data) && setServerPhotos(r.data))
      .catch(() => setServerPhotos([]));
  }, []);
  const photos = useMemo(() => {
    if (!serverPhotos || serverPhotos.length === 0) return EVENT_PHOTOS;
    // Server-first, then fallback for any not yet migrated. Cap at layout length.
    const sids = new Set(serverPhotos.map((p) => p.id));
    const merged = [
      ...serverPhotos.map((p) => ({ ...p, src: p.url, id: p.id })),
      ...EVENT_PHOTOS.filter((p) => !sids.has(p.id)),
    ];
    return merged;
  }, [serverPhotos]);

  // Layout classes chosen individually per tile to build an asymmetric editorial wall.
  // Row-span-2 tiles pair with two half-height tiles beside them so every cell in the
  // grid is filled — no orphan gaps.
  const layout = [
    "lg:col-span-4 lg:row-span-2",   // tall left — spans both rows
    "lg:col-span-4",                  // wide top-mid
    "lg:col-span-4",                  // wide top-right
    "lg:col-span-4",                  // wide bottom-mid
    "lg:col-span-4",                  // wide bottom-right
  ];

  return (
    <section
      className="bg-[#0B0B0C] border-t border-[#343437] relative grain"
      data-testid="in-the-field"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14 items-end">
          <div className="lg:col-span-7">
            <p className="eyebrow">In the field</p>
            <h2 className="mt-5 font-display text-5xl md:text-6xl leading-[0.98] tracking-tight text-[#FAF9F6]">
              Programs{" "}<br />
              <span className="text-[#D8A936]">in the wild.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[#A9A9A6] leading-relaxed max-w-md">
              Snapshots from 12 years of student programs, festivals, cultural activations and community
              events across Australia and Canada. Real crowds. Real scale. Real work.
            </p>
          </div>
        </div>

        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-3 md:gap-4 lg:[grid-auto-rows:220px]"
        >
          {photos.slice(0, layout.length).map((p, i) => (
            <motion.figure
              key={p.id}
              initial={{ y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.06 }}
              className={`group relative overflow-hidden border border-[#343437] bg-[#151517] min-h-[220px] lg:min-h-0 ${layout[i] || "lg:col-span-4"}`}
              data-testid={`field-photo-${p.id}`}
            >
              <img
                src={p.src}
                alt={p.caption}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
              />
              {/* Bottom gradient overlay for legibility */}
              <div
                className="absolute inset-x-0 bottom-0 h-2/3 pointer-events-none"
                style={{
                  background: "linear-gradient(to top, rgba(11,11,12,0.85) 0%, rgba(11,11,12,0) 100%)",
                }}
              />
              <figcaption className="absolute left-5 right-5 bottom-5 flex items-end justify-between gap-3">
                <div>
                  <p className="font-display text-[15px] md:text-base text-[#FAF9F6] leading-tight tracking-tight">
                    {p.caption}
                  </p>
                  <p className="mt-1 text-[10px] tracking-[0.22em] uppercase text-[#D8A936]">
                    {p.meta}
                  </p>
                </div>
              </figcaption>

              {/* Hover badge with event / year / location */}
              {(p.event || p.year || p.location) && (
                <div
                  data-testid={`field-photo-credit-${p.id}`}
                  className="absolute top-4 right-4 max-w-[220px] opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-300 bg-[#0B0B0C]/95 backdrop-blur-sm border border-[#D8A936]/60 px-3 py-2.5 pointer-events-none"
                >
                  {p.event && (
                    <p className="text-[11px] text-[#FAF9F6] leading-snug font-medium">
                      {p.event}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-1.5">
                    {p.year && (
                      <span className="text-[10px] tracking-[0.14em] uppercase text-[#D8A936]">{p.year}</span>
                    )}
                    {p.year && p.location && <span className="text-[10px] text-[#6E6E6B]">·</span>}
                    {p.location && (
                      <span className="text-[10px] text-[#A9A9A6] leading-tight">{p.location}</span>
                    )}
                  </div>
                </div>
              )}
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}
